import { Injectable } from '@angular/core';
import { HttpParams } from '@angular/common/http';
import { BaseCrudService } from '../../../core/data/services/base-crud.service';
import { Observable } from 'rxjs';
import {
  ChangeEmployeeBranchDto,
  ChangeEmployeeManagerDto,
  EmployeeDto,
  EmployeeListParams,
  EmployeeStatsDto,
  PlatformUserDto,
  PlatformUserFilter,
  EmployeeStatusChangeRequestDto,
  UpdateEmployeeDto,
} from '../../../core/data/new-dtos/employee.dtos';

@Injectable({ providedIn: 'root' })
export class EmployeesService extends BaseCrudService<EmployeeDto> {
  controller = 'Employees';

  // GET /api/Employees - scoped to the caller's role and branches by the API
  getScoped(params: EmployeeListParams = {}): Observable<EmployeeDto[]> {
    let httpParams = new HttpParams();
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        httpParams = httpParams.set(key, String(value));
      }
    });

    return this.httpClient.get<EmployeeDto[]>(this.controller, {
      params: httpParams,
    });
  }

  // GET /api/Employees/all - superusers only
  getPlatformUsers(filter: PlatformUserFilter = {}): Observable<PlatformUserDto[]> {
    let params = new HttpParams();
    if (filter.bankId != null) params = params.set('bankId', filter.bankId);
    if (filter.role) params = params.set('role', filter.role);
    if (filter.status) params = params.set('status', filter.status);
    if (filter.search) params = params.set('search', filter.search);
    return this.httpClient.get<PlatformUserDto[]>(`${this.controller}/all`, { params });
  }

  // GET /api/Employees/{employeeId}
  getEmployee(employeeId: number): Observable<EmployeeDto> {
    return this.httpClient.get<EmployeeDto>(`${this.controller}/${employeeId}`);
  }

  // GET /api/Employees/{employeeId}/stats
  getStats(employeeId: number): Observable<EmployeeStatsDto> {
    return this.httpClient.get<EmployeeStatsDto>(`${this.controller}/${employeeId}/stats`);
  }

  // PUT /api/Employees/{employeeId}
  updateEmployee(employeeId: number, body: UpdateEmployeeDto): Observable<EmployeeDto> {
    return this.httpClient.put<EmployeeDto>(`${this.controller}/${employeeId}`, body);
  }

  // PATCH /api/Employees/{employeeId}/status
  changeStatus(
    employeeId: number,
    body: EmployeeStatusChangeRequestDto,
  ): Observable<EmployeeDto> {
    return this.httpClient.patch<EmployeeDto>(
      `${this.controller}/${employeeId}/status`,
      body,
    );
  }

  // PATCH /api/Employees/{employeeId}/branch
  changeBranch(employeeId: number, body: ChangeEmployeeBranchDto): Observable<EmployeeDto> {
    return this.httpClient.patch<EmployeeDto>(
      `${this.controller}/${employeeId}/branch`,
      body,
    );
  }

  // PATCH /api/Employees/{employeeId}/manager
  changeManager(employeeId: number, body: ChangeEmployeeManagerDto): Observable<EmployeeDto> {
    return this.httpClient.patch<EmployeeDto>(
      `${this.controller}/${employeeId}/manager`,
      body,
    );
  }

  // DELETE /api/Employees/{employeeId} - soft removal, returns the updated employee
  remove(employeeId: number): Observable<EmployeeDto> {
    return this.httpClient.delete<EmployeeDto>(`${this.controller}/${employeeId}`);
  }
}
